import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardText, Plus, LinkSimple, Trash, Copy, Eye, FilePdf, FileArrowDown,
         MagicWand, CheckCircle, Clock, Warning, X, House, User as UserIcon, Broadcast,
         PencilSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

const KIND_LABEL = { move_in: "Move-In", move_out: "Move-Out", periodic: "Periodic", custom: "Custom" };
const KIND_COLOR = { move_in: "bg-emerald-600", move_out: "bg-rose-600", periodic: "bg-cyan-600", custom: "bg-slate-600" };
const AUDIENCE_LABEL = { tenant: "Tenant-Guided", internal: "Worker / Leasing" };
const AUDIENCE_COLOR = { tenant: "bg-cyan-100 text-cyan-900 border-cyan-500", internal: "bg-violet-100 text-violet-900 border-violet-500" };
const STATUS_META = {
  draft:       { label: "Not Started", bg: "bg-slate-500 text-white", Icon: Clock },
  in_progress: { label: "In Progress", bg: "bg-amber-500 text-white", Icon: Warning },
  completed:   { label: "Completed",   bg: "bg-emerald-600 text-white", Icon: CheckCircle },
};

export default function AdminInspections() {
  const [tab, setTab] = useState("inspections");
  return (
    <AdminShell>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Broadcast weight="fill" size={30} className="text-cyan-600"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">TenantRadar™</h1>
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Send a mobile inspection link to a tenant, worker, or leasing rep. They walk each room, add photos + condition ratings, and you get a branded PDF report back.
        </p>

        <div className="mt-5 flex gap-1 border-b-2 border-[#0f172a]" data-testid="inspections-tabs">
          <TabBtn active={tab === "inspections"} onClick={() => setTab("inspections")} testid="tab-inspections">Inspections</TabBtn>
          <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testid="tab-templates">Templates</TabBtn>
        </div>

        <div className="mt-5">
          {tab === "inspections" ? <InspectionsTab/> : <TemplatesTab/>}
        </div>
      </div>
    </AdminShell>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-11 px-5 font-black uppercase tracking-widest text-xs border-2 border-b-0 ${
        active ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0f172a]"
      }`}>
      {children}
    </button>
  );
}

function InspectionsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editAddr, setEditAddr] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/tenant-inspections"); setRows(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this inspection? Any photos will be kept in storage.")) return;
    try { await api.delete(`/tenant-inspections/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const copyLink = (row) => {
    const url = `${window.location.origin}/tenant-inspection/${row.magic_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Portal link copied");
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditAddr(row.property_address || "");
    setEditUnit(row.unit || "");
  };
  const cancelEdit = () => {
    setEditingId(null); setEditAddr(""); setEditUnit("");
  };
  const saveEdit = async () => {
    if (!editAddr.trim()) { toast.error("Property address is required"); return; }
    setEditSaving(true);
    try {
      await api.patch(`/tenant-inspections/${editingId}`, {
        property_address: editAddr.trim(),
        unit: editUnit.trim(),
      });
      toast.success("Inspection relabeled");
      cancelEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setEditSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          {rows.length} inspection{rows.length === 1 ? "" : "s"}
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-inspection-btn"
          className="tactile-btn h-10 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
          <Plus weight="bold" size={14}/> New Inspection
        </button>
      </div>

      {loading && <div className="mt-6 text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="mt-6 rugged-card p-10 text-center">
          <ClipboardText size={48} className="mx-auto text-slate-300"/>
          <div className="mt-3 font-display font-black text-lg uppercase text-slate-500">No inspections yet</div>
          <div className="mt-1 text-sm text-slate-500">Create a template first, then send the tenant a mobile link.</div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="inspection-rows">
          {rows.map(row => {
            const sm = STATUS_META[row.status] || STATUS_META.draft;
            return (
              <div key={row.id} data-testid={`inspection-row-${row.id}`}
                className="rugged-card p-4 flex flex-wrap gap-3 items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest ${sm.bg}`}>
                  <sm.Icon weight="fill" size={12}/> {sm.label}
                </span>
                <span className={`inline-flex items-center px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${KIND_COLOR[row.template_kind] || "bg-slate-600"}`}>
                  {KIND_LABEL[row.template_kind] || row.template_kind}
                </span>
                <div className="flex-1 min-w-[240px]">
                  {editingId === row.id ? (
                    <div className="space-y-1" data-testid={`inline-edit-${row.id}`}>
                      <div className="flex gap-1">
                        <input list="known-addresses" autoComplete="off" value={editAddr} onChange={e => setEditAddr(e.target.value)}
                          autoFocus data-testid={`inline-addr-${row.id}`}
                          placeholder="Property address"
                          className="flex-1 h-8 border-2 border-amber-500 px-2 text-sm font-medium"/>
                        <input value={editUnit} onChange={e => setEditUnit(e.target.value)}
                          data-testid={`inline-unit-${row.id}`}
                          placeholder="Unit"
                          className="w-20 h-8 border-2 border-amber-500 px-2 text-sm font-black"/>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={saveEdit} disabled={editSaving || !editAddr.trim()}
                          data-testid={`inline-save-${row.id}`}
                          className="flex-1 h-7 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest">
                          {editSaving ? "Saving…" : "Save"}
                        </button>
                        <button onClick={cancelEdit} disabled={editSaving}
                          data-testid={`inline-cancel-${row.id}`}
                          className="flex-1 h-7 border-2 border-slate-400 font-black uppercase text-[10px] tracking-widest">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-black text-sm">{row.property_address}{row.unit ? ` · Unit ${row.unit}` : ""}</div>
                      <div className="text-xs text-slate-600 mt-0.5 inline-flex items-center gap-1">
                        <UserIcon size={12}/> {row.tenant_name || "(no tenant name)"}
                        {row.completed_at && <span className="ml-3">· Signed by <b>{row.signature_name}</b> · {new Date(row.completed_at).toLocaleDateString()}</span>}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {editingId !== row.id && (
                    <button onClick={() => startEdit(row)} data-testid={`edit-unit-${row.id}`} title="Edit unit / address"
                      className="h-9 w-9 border-2 border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white inline-flex items-center justify-center">
                      <PencilSimple size={16}/>
                    </button>
                  )}
                  <button onClick={() => copyLink(row)} data-testid={`copy-link-${row.id}`} title="Copy tenant link"
                    className="h-9 w-9 border-2 border-[#0f172a] hover:bg-[#0f172a] hover:text-white inline-flex items-center justify-center">
                    <Copy size={16}/>
                  </button>
                  <button onClick={() => setViewing(row.id)} data-testid={`view-${row.id}`} title="View inspection"
                    className="h-9 w-9 border-2 border-[#0f172a] hover:bg-[#0f172a] hover:text-white inline-flex items-center justify-center">
                    <Eye size={16}/>
                  </button>
                  <a href={`${api.defaults.baseURL}/tenant-inspections/${row.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`}
                    target="_blank" rel="noreferrer" data-testid={`pdf-${row.id}`} title="Download PDF"
                    className="h-9 w-9 border-2 border-[#0f172a] hover:bg-[#0f172a] hover:text-white inline-flex items-center justify-center">
                    <FilePdf size={16}/>
                  </a>
                  <button onClick={() => del(row.id)} data-testid={`delete-${row.id}`} title="Delete"
                    className="h-9 w-9 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center justify-center">
                    <Trash size={16}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>}
      {viewing && <ViewModal id={viewing} onClose={() => setViewing(null)}/>}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ template_id: "", property_address: "", unit: "",
                                      tenant_name: "", tenant_email: "", tenant_phone: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/inspection-templates").then(r => setTemplates(r.data)).catch(() => {}); }, []);

  const selectedTpl = templates.find(t => t.id === form.template_id);
  const audience = selectedTpl?.audience || "tenant";
  const isInternal = audience === "internal";
  // Group templates by audience for the select
  const tenantTemplates = templates.filter(t => (t.audience || "tenant") === "tenant");
  const internalTemplates = templates.filter(t => t.audience === "internal");

  const submit = async () => {
    if (!form.template_id) { toast.error("Choose a template"); return; }
    if (!form.property_address.trim()) { toast.error("Property address is required"); return; }
    if (!form.tenant_name.trim()) { toast.error(isInternal ? "Inspector name is required" : "Tenant name is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/tenant-inspections", form);
      setResult(r.data);
      toast.success("Inspection created");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const copyUrl = () => {
    if (!result?.magic_token) return;
    const url = `${window.location.origin}/tenant-inspection/${result.magic_token}`;
    navigator.clipboard.writeText(url);
    toast.success(isInternal ? "Link copied — give it to the worker/leasing rep" : "Link copied — text or email it to the tenant");
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="bg-[#0f172a] text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">New Inspection</div>
          <button onClick={onClose} className="p-1 hover:bg-white/20" data-testid="close-create"><X size={20}/></button>
        </div>
        {!result ? (
          <div className="p-5 space-y-3">
            {templates.length === 0 && (
              <div className="p-3 border-2 border-amber-500 bg-amber-50 text-xs font-bold text-amber-900">
                No templates yet. Switch to the <b>Templates</b> tab and click <b>Seed Default Templates</b> first.
              </div>
            )}
            <Field label="Template">
              <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))}
                data-testid="template-select" className="w-full h-11 px-3 border-2 border-[#0f172a] font-bold text-sm">
                <option value="">— Choose —</option>
                {tenantTemplates.length > 0 && (
                  <optgroup label="TENANT-GUIDED (Periodic / Custom)">
                    {tenantTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({KIND_LABEL[t.kind] || t.kind})</option>)}
                  </optgroup>
                )}
                {internalTemplates.length > 0 && (
                  <optgroup label="WORKER / LEASING TEAM (Move-In / Move-Out)">
                    {internalTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({KIND_LABEL[t.kind] || t.kind})</option>)}
                  </optgroup>
                )}
              </select>
            </Field>
            {selectedTpl && (
              <div className={`p-2 border-2 text-[11px] font-bold ${AUDIENCE_COLOR[audience]}`} data-testid="audience-hint">
                {isInternal
                  ? "This is an INTERNAL inspection — the link goes to a worker or leasing team member's phone, not the tenant."
                  : "This is a TENANT-GUIDED inspection — send the link to the tenant; they walk each room on their phone."}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Field label="Property Address">
                  <input value={form.property_address} onChange={e => setForm(f => ({ ...f, property_address: e.target.value }))}
                    data-testid="property-address" placeholder="1346 S 30th St"
                    className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
                </Field>
              </div>
              <Field label="Unit">
                <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                  data-testid="property-unit" placeholder="2R"
                  className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
              </Field>
            </div>
            <Field label={isInternal ? "Inspector Name (worker or leasing rep)" : "Tenant Name"}>
              <input value={form.tenant_name} onChange={e => setForm(f => ({ ...f, tenant_name: e.target.value }))}
                data-testid="tenant-name"
                className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Bedrooms in this Unit">
                <div className="flex items-center gap-1 h-11 border-2 border-[#0f172a] bg-white px-2" data-testid="bedrooms-field">
                  <button type="button" onClick={() => setForm(f => ({ ...f, bedrooms: Math.max(0, f.bedrooms - 1) }))}
                    data-testid="bedrooms-minus"
                    className="h-8 w-8 bg-slate-100 hover:bg-slate-200 font-black text-lg">−</button>
                  <input type="number" min={0} max={12} value={form.bedrooms}
                    onChange={e => setForm(f => ({ ...f, bedrooms: Math.max(0, Math.min(12, parseInt(e.target.value || "0"))) }))}
                    data-testid="bedrooms-input"
                    className="flex-1 h-9 text-center font-display font-black text-xl w-10 min-w-0"/>
                  <button type="button" onClick={() => setForm(f => ({ ...f, bedrooms: Math.min(12, f.bedrooms + 1) }))}
                    data-testid="bedrooms-plus"
                    className="h-8 w-8 bg-slate-100 hover:bg-slate-200 font-black text-lg">+</button>
                </div>
              </Field>
              <Field label="Bathrooms in this Unit">
                <div className="flex items-center gap-1 h-11 border-2 border-[#0f172a] bg-white px-2" data-testid="bathrooms-field">
                  <button type="button" onClick={() => setForm(f => ({ ...f, bathrooms: Math.max(0, f.bathrooms - 1) }))}
                    data-testid="bathrooms-minus"
                    className="h-8 w-8 bg-slate-100 hover:bg-slate-200 font-black text-lg">−</button>
                  <input type="number" min={0} max={8} value={form.bathrooms}
                    onChange={e => setForm(f => ({ ...f, bathrooms: Math.max(0, Math.min(8, parseInt(e.target.value || "0"))) }))}
                    data-testid="bathrooms-input"
                    className="flex-1 h-9 text-center font-display font-black text-xl w-10 min-w-0"/>
                  <button type="button" onClick={() => setForm(f => ({ ...f, bathrooms: Math.min(8, f.bathrooms + 1) }))}
                    data-testid="bathrooms-plus"
                    className="h-8 w-8 bg-slate-100 hover:bg-slate-200 font-black text-lg">+</button>
                </div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label={isInternal ? "Inspector Email (optional)" : "Tenant Email (optional)"}>
                <input value={form.tenant_email} onChange={e => setForm(f => ({ ...f, tenant_email: e.target.value }))}
                  data-testid="tenant-email" type="email"
                  className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
              </Field>
              <Field label={isInternal ? "Inspector Phone (optional)" : "Tenant Phone (optional)"}>
                <input value={form.tenant_phone} onChange={e => setForm(f => ({ ...f, tenant_phone: e.target.value }))}
                  data-testid="tenant-phone"
                  className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
              </Field>
            </div>
            <button onClick={submit} disabled={busy}
              data-testid="create-submit"
              className="w-full h-11 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-black uppercase tracking-widest text-xs">
              {busy ? "Creating…" : `Generate ${isInternal ? "Inspector" : "Tenant"} Link`}
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="p-3 border-2 border-emerald-500 bg-emerald-50">
              <div className="font-black uppercase tracking-widest text-emerald-800 text-xs">Inspection Created</div>
              <div className="mt-2 text-sm">
                {result.audience === "internal"
                  ? "Give this link to the worker or leasing rep. They open it on their phone, walk each room, and submit."
                  : "Send the tenant this link. They walk each room on their phone, no app install needed."}
              </div>
              <div className="mt-3 p-2 border-2 border-[#0f172a] bg-white break-all font-mono text-xs" data-testid="portal-url">
                {`${window.location.origin}/tenant-inspection/${result.magic_token}`}
              </div>
              <button onClick={copyUrl} data-testid="copy-portal-url"
                className="mt-3 w-full h-10 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                <LinkSimple weight="bold" size={14}/> Copy Link
              </button>
            </div>
            <button onClick={onCreated} className="w-full h-11 border-2 border-[#0f172a] font-black uppercase tracking-widest text-xs">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function ViewModal({ id, onClose }) {
  const [insp, setInsp] = useState(null);
  const [err, setErr] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [signBusy, setSignBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState({}); // path -> bool
  const [editOpen, setEditOpen] = useState(false);
  const [editAddr, setEditAddr] = useState("");
  const [editUnit, setEditUnit] = useState("");
  const [editTenant, setEditTenant] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const load = () => api.get(`/tenant-inspections/${id}`)
    .then(r => {
      setInsp(r.data);
      setEditAddr(r.data.property_address || "");
      setEditUnit(r.data.unit || "");
      setEditTenant(r.data.tenant_name || "");
    })
    .catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"));
  useEffect(() => { load(); }, [id]);

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      await api.patch(`/tenant-inspections/${id}`, {
        property_address: editAddr.trim(),
        unit: editUnit.trim(),
        tenant_name: editTenant.trim(),
      });
      toast.success("Inspection details updated");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setEditBusy(false); }
  };

  const streamUrl = (path) => insp && `${api.defaults.baseURL}/public/tenant-inspection/${insp.magic_token}/photo/${path}`;

  // Prefer the rich turn/sweep report renderer when we have room-by-room data (move-out sweep, turn, etc.)
  const hasSweepData = insp && (insp.is_turn || (insp.inspection_type && ["move_out","move_in","periodic"].includes(String(insp.inspection_type).toLowerCase())));

  const scanAll = async () => {
    if (!window.confirm("Run Claude Vision on every photo in this inspection? This may take a minute for large inspections.")) return;
    setScanBusy(true);
    setAiSummary(null);
    try {
      const r = await api.post(`/tenant-inspections/${id}/ai-scan-all`);
      setAiSummary(r.data.summary);
      toast.success(`Scanned ${r.data.summary.scanned} photo${r.data.summary.scanned === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI scan failed");
    } finally { setScanBusy(false); }
  };

  const classifyOne = async (path, room_key, item_key) => {
    setPhotoBusy(b => ({ ...b, [path]: true }));
    try {
      await api.post(`/tenant-inspections/${id}/photo/classify-damage`, { path, room_key, item_key });
      toast.success("Photo classified");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Classify failed");
    } finally {
      setPhotoBusy(b => ({ ...b, [path]: false }));
    }
  };

  const submitSign = async () => {
    if (!signName.trim()) { toast.error("Signature name required"); return; }
    setSignBusy(true);
    try {
      await api.post(`/tenant-inspections/${id}/admin-complete`, { signature_name: signName.trim() });
      toast.success("Inspection completed");
      setSignOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Complete failed");
    } finally { setSignBusy(false); }
  };

  const aiKey = (p) => p.replace(/\./g, "_DOT_").replace(/\//g, "_SL_");
  const clsBadge = { tenant_damage: "bg-red-700", wear_and_tear: "bg-emerald-600",
                     preexisting: "bg-slate-500", ambiguous: "bg-amber-500" };
  const clsLabel = { tenant_damage: "DMG", wear_and_tear: "W&T", preexisting: "PRE", ambiguous: "?" };

  const reports = insp?.ai_damage_reports || {};

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0f172a] text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">Inspection Detail</div>
          <button onClick={onClose} className="p-1 hover:bg-white/20" data-testid="close-view"><X size={22}/></button>
        </div>
        {err && <div className="p-6 text-red-600 font-bold">{err}</div>}
        {!err && !insp && <div className="p-6 text-slate-500 font-bold uppercase tracking-widest">Loading…</div>}
        {insp && (
          <div className="p-5 space-y-4" data-testid="inspection-view">
            <div>
              <div className="font-display font-black text-2xl">{insp.template_name}</div>
              {!editOpen ? (
                <>
                  <div className="text-sm text-slate-700 mt-1 inline-flex items-center gap-1"><House size={14}/> {insp.property_address}{insp.unit ? ` · Unit ${insp.unit}` : ""}</div>
                  <div className="text-sm text-slate-700 mt-0.5 inline-flex items-center gap-1"><UserIcon size={13}/> {insp.tenant_name}</div>
                  <button onClick={() => setEditOpen(true)} data-testid="edit-inspection-btn"
                    className="mt-2 h-8 px-3 border-2 border-[#0f172a] hover:bg-slate-100 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                    <PencilSimple weight="bold" size={12}/> Edit Unit / Tenant
                  </button>
                </>
              ) : (
                <div className="mt-2 border-2 border-amber-500 bg-amber-50 p-3 space-y-2" data-testid="edit-inspection-form">
                  <div className="font-black uppercase text-[10px] tracking-widest text-amber-900">✎ Editing — photos & responses stay put</div>
                  <div>
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Property address</label>
                    <input value={editAddr} onChange={e => setEditAddr(e.target.value)} data-testid="edit-addr-input"
                      className="w-full h-9 border-2 border-[#0f172a] px-2 text-sm mt-1"/>
                  </div>
                  <div>
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Unit</label>
                    <input value={editUnit} onChange={e => setEditUnit(e.target.value)} data-testid="edit-unit-input"
                      placeholder="e.g. 9A"
                      className="w-full h-9 border-2 border-[#0f172a] px-2 text-sm mt-1"/>
                  </div>
                  <div>
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Tenant name</label>
                    <input value={editTenant} onChange={e => setEditTenant(e.target.value)} data-testid="edit-tenant-input"
                      className="w-full h-9 border-2 border-[#0f172a] px-2 text-sm mt-1"/>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditOpen(false)} disabled={editBusy}
                      className="flex-1 h-9 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest">
                      Cancel
                    </button>
                    <button onClick={saveEdit} disabled={editBusy || !editAddr.trim()}
                      data-testid="edit-save-btn"
                      className="flex-1 h-9 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black uppercase text-[10px] tracking-widest">
                      {editBusy ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              )}
              {insp.status === "completed" && (
                <div className="mt-2 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-700">
                  <CheckCircle weight="fill" size={16}/> Completed by {insp.signature_name} · {new Date(insp.completed_at).toLocaleString()}
                  {insp.admin_completed && <span className="px-1.5 py-0.5 bg-violet-600 text-white text-[9px] tracking-widest">ADMIN-SIGNED</span>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <a href={`${api.defaults.baseURL}/tenant-inspections/${insp.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("token") || localStorage.getItem("access_token") || "")}`}
                target="_blank" rel="noreferrer" data-testid="view-pdf"
                className="h-11 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:opacity-90">
                <FileArrowDown weight="bold" size={14}/> PDF Report
              </a>
              {insp.status !== "completed" && (
                <button onClick={() => setSignOpen(true)} data-testid="admin-mark-complete-btn"
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                  <CheckCircle weight="fill" size={14}/> Mark Complete & Sign
                </button>
              )}
              <button onClick={scanAll} disabled={scanBusy} data-testid="ai-scan-all-btn"
                className="h-11 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
                <MagicWand weight="fill" size={14}/> {scanBusy ? "Scanning…" : "Scan All w/ AI"}
              </button>
            </div>

            {aiSummary && (
              <div className="p-3 border-2 border-fuchsia-600 bg-fuchsia-50 space-y-1" data-testid="ai-scan-summary">
                <div className="font-display font-black uppercase tracking-tight text-sm">🤖 AI Damage Report</div>
                <div className="text-xs text-slate-700">
                  Scanned <b>{aiSummary.scanned}</b> of {aiSummary.total} photos · Skipped {aiSummary.skipped_existing} already-classified · {aiSummary.errors} error{aiSummary.errors === 1 ? "" : "s"}
                </div>
                <div className="grid grid-cols-4 gap-1 text-[10px] font-black uppercase tracking-widest mt-1">
                  <div className="p-1 bg-red-700 text-white text-center">Damage {aiSummary.tenant_damage}</div>
                  <div className="p-1 bg-emerald-600 text-white text-center">W&T {aiSummary.wear_and_tear}</div>
                  <div className="p-1 bg-slate-500 text-white text-center">Pre {aiSummary.preexisting}</div>
                  <div className="p-1 bg-amber-500 text-white text-center">Amb {aiSummary.ambiguous}</div>
                </div>
                <div className="text-sm mt-1">
                  Suggested chargeback range: <b className="text-red-700">${(aiSummary.chargeable_low_cents/100).toFixed(0)} – ${(aiSummary.chargeable_high_cents/100).toFixed(0)}</b>
                </div>
              </div>
            )}

            {insp.rooms.map(room => (
              <div key={room.key} className="border-2 border-slate-200 p-3">
                <div className="font-display font-black text-lg uppercase">{room.name}</div>
                <div className="mt-2 space-y-3">
                  {room.items.map(item => {
                    const resp = insp.responses[`${room.key}.${item.key}`] || {};
                    const cond = resp.condition || "";
                    const condColor = { excellent: "bg-emerald-600", good: "bg-sky-600", fair: "bg-amber-500",
                                        poor: "bg-red-600", damaged: "bg-red-900", na: "bg-slate-400" }[cond] || "bg-slate-300";
                    return (
                      <div key={item.key} className="border-t pt-2" data-testid={`view-item-${room.key}-${item.key}`}>
                        <div className="flex items-start gap-2 flex-wrap">
                          <div className="flex-1 min-w-[200px] text-sm font-bold">{item.label}</div>
                          <span className={`inline-flex items-center px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${condColor}`}>
                            {cond ? cond : "Not Answered"}
                          </span>
                        </div>
                        {resp.notes && <div className="mt-1 text-xs italic text-slate-700">{resp.notes}</div>}
                        {(resp.photos || []).length > 0 && (
                          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                            {resp.photos.map((p, i) => {
                              const ai = reports[aiKey(p.path)];
                              const busyOne = photoBusy[p.path];
                              return (
                                <div key={i} className="relative flex-shrink-0" data-testid={`view-photo-wrap-${room.key}-${item.key}-${i}`}>
                                  <img src={streamUrl(p.path)} alt="" data-testid={`view-photo-${room.key}-${item.key}-${i}`}
                                    className="h-24 w-24 object-cover border-2 border-[#0f172a]"/>
                                  {ai && (
                                    <span className={`absolute top-0 left-0 px-1 py-0.5 text-[9px] font-black uppercase tracking-wider text-white ${clsBadge[ai.classification] || "bg-slate-500"}`}
                                          title={`${ai.classification} · ${ai.severity} · ${ai.confidence}${ai.chargeable ? ` · $${ai.suggested_low}-${ai.suggested_high}` : ""}\n${ai.reasoning}`}
                                          data-testid={`ai-badge-${room.key}-${item.key}-${i}`}>
                                      {clsLabel[ai.classification] || "?"}
                                    </span>
                                  )}
                                  {!ai && (
                                    <button onClick={() => classifyOne(p.path, room.key, item.key)} disabled={busyOne}
                                      data-testid={`ai-classify-btn-${room.key}-${item.key}-${i}`}
                                      title="Classify with AI"
                                      className="absolute bottom-0 right-0 h-6 w-6 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white text-[10px] font-black inline-flex items-center justify-center">
                                      {busyOne ? "…" : "AI"}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {signOpen && (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4" onClick={() => !signBusy && setSignOpen(false)}>
          <div className="bg-white border-4 border-[#0f172a] max-w-md w-full p-5" onClick={e => e.stopPropagation()} data-testid="admin-sign-modal">
            <div className="font-display font-black text-xl uppercase mb-2">Mark Complete & Sign</div>
            <div className="text-xs text-slate-600 mb-3">
              This closes the inspection without waiting on the tenant. Your name will appear as the signer, flagged <b>ADMIN-SIGNED</b> on the report.
            </div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Signature name</label>
            <input value={signName} onChange={e => setSignName(e.target.value)} autoFocus
              data-testid="admin-sign-name-input"
              placeholder="e.g. Andrew Harman (PMR)"
              className="w-full h-11 border-2 border-[#0f172a] px-3 text-sm mt-1"/>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setSignOpen(false)} disabled={signBusy}
                className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest">
                Cancel
              </button>
              <button onClick={submitSign} disabled={signBusy || !signName.trim()}
                data-testid="admin-sign-submit"
                className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest">
                {signBusy ? "Signing…" : "Sign & Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/inspection-templates"); setRows(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inspection-templates/seed-defaults");
      toast.success(r.data.count > 0 ? `Created ${r.data.count} default template(s)` : "Defaults already exist");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/inspection-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
          {rows.length} template{rows.length === 1 ? "" : "s"}
        </div>
        <button onClick={seedDefaults} disabled={busy} data-testid="seed-defaults-btn"
          className="tactile-btn h-10 px-4 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
          <MagicWand weight="fill" size={14}/> {busy ? "Working…" : "Seed Default Templates"}
        </button>
      </div>

      {loading && <div className="mt-6 text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="mt-6 rugged-card p-10 text-center">
          <ClipboardText size={48} className="mx-auto text-slate-300"/>
          <div className="mt-3 font-display font-black text-lg uppercase text-slate-500">No templates yet</div>
          <div className="mt-1 text-sm text-slate-500">Click <b>Seed Default Templates</b> to create Move-In, Move-Out, and Periodic templates in one click.</div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="template-rows">
          {rows.map(t => {
            const itemCount = (t.rooms || []).reduce((n, r) => n + (r.items?.length || 0), 0);
            return (
              <div key={t.id} data-testid={`template-${t.id}`}
                className="rugged-card p-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-black text-lg uppercase">{t.name}</div>
                    <div className="text-xs text-slate-600">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase text-white ${KIND_COLOR[t.kind]}`}>{KIND_LABEL[t.kind] || t.kind}</span>
                      <span className="ml-2 font-bold">{(t.rooms || []).length} rooms · {itemCount} items</span>
                    </div>
                  </div>
                  <button onClick={() => del(t)} data-testid={`del-template-${t.id}`} title="Delete"
                    className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                </div>
                <ul className="text-xs text-slate-700 mt-1">
                  {(t.rooms || []).map(r => (
                    <li key={r.key}>• {r.name} <span className="text-slate-400">({r.items?.length || 0})</span></li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
